import { HStack, Button, useBoolean, Icon } from '@wegroup/design-system';
import cogoToast from 'cogo-toast';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import LicenseInputEU from '../../../../../../common/src/legacy/LicenseInputEU';
import { retrieveVehiclesByNumberPlate } from '../../../../../../common/src/legacy/services/apiRouterService';
import { request } from '../../../../../../common/src/legacy/services/httpSessionService';
import { Car } from '../../../../../../../types/SMT';
import { Inputs } from '../../../../types/Enums';
import { getFlowsDataTestId } from '../../../../utils/testUtils';
import { CarResponseData } from '../../../../../../../types/VehicleResponse';

interface Props {
  name?: string;
  value: string;
  onChange: (arg0: Car, numberPlate: string, allData: CarResponseData) => void;
  isDisabled?: boolean;
  /** Optional regex string provided by backend for client-side validation */
  regex?: string;
  /** Optional localized error message for regex mismatch */
  regexErrorMessage?: string;
}

const NumberPlateInput: React.FC<Props> = ({
  name,
  value,
  isDisabled,
  onChange,
  regex,
  regexErrorMessage,
}) => {
  const { t } = useTranslation();

  const [numberPlate, setNumberPlate] = useState<string>(value);
  const [isSearching, setIsSearching] = useBoolean();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNumberPlate(e.target.value);
  };

  const handleClick = async () => {
    setIsSearching.on();
    const trimmedPlate = numberPlate?.trim();
    const normalizedPlate = (trimmedPlate || '').replace(/\s+/g, '');

    // Front-end validation using backend-provided regex
    if (regex) {
      try {
        // Ensure the regex matches the full input by anchoring when backend regex is unanchored
        const anchored =
          regex.startsWith('^') || regex.endsWith('$') ? regex : `^(${regex})$`;
        let pattern: RegExp | null = null;
        try {
          pattern = new RegExp(anchored);
        } catch {
          // Attempt to escape hyphens which may cause invalid ranges
          const escapedHyphens = anchored.replace(/-/g, '\\-');
          pattern = new RegExp(escapedHyphens);
        }

        if (pattern && !pattern.test(normalizedPlate)) {
          cogoToast.error(regexErrorMessage || t('INVALID-FORMAT'));
          setIsSearching.off();
          return;
        }
      } catch (e) {
        // If the provided regex is invalid, skip validation and proceed
      }
    }

    const [resp, status] = await retrieveVehiclesByNumberPlate(
      trimmedPlate,
      'NL',
    );

    if (status >= 400) {
      cogoToast.error(t('COMPANY-SCAN_INPUT_NUMBER-PLATE-SEARCH_NOT-FOUND'));
    } else {
      const [premiumResp, premiumStatus] = await request({
        path: resp.items[0].self,
        method: 'GET',
      });

      if (premiumStatus >= 400) {
        cogoToast.error(t('COMPANY-SCAN_INPUT_NUMBER-PLATE-SEARCH_NOT-FOUND'));
      }

      onChange(
        premiumResp?.domain_car?.car as Car,
        trimmedPlate,
        premiumResp?.data as CarResponseData,
      );
    }
    setIsSearching.off();
  };

  return (
    <HStack spacing="4">
      <LicenseInputEU
        countryCode="NL"
        onChange={handleChange}
        value={numberPlate}
        isDisabled={isDisabled}
      />
      <Button
        leftIcon={<Icon name="IconSearch" color="currentcolor" />}
        h="12"
        px="6"
        onClick={handleClick}
        isLoading={isSearching}
        data-test-id={getFlowsDataTestId(
          Inputs.NUMBER_PLATE,
          name || '',
          'SEARCH-BUTTON',
        )}
        isDisabled={isDisabled}
      >
        {t('SEARCH')}
      </Button>
    </HStack>
  );
};

export default NumberPlateInput;
